import { useMemo, useEffect, useState } from 'react'

import { useForm, FormProvider } from 'react-hook-form'


import { useDigitalProductStore } from '@/features/store/hook'
import { partitionRewardRequests } from '@/features/store/lib/partitionRewardRequests'
import { useBottomSheetContext } from '@/shared/contexts/BottomSheetContext'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useRecords } from '@/shared/hooks'
import { MobileSheet } from '@/shared/ui/MobileSheet'

import { DigitalProductStoreWithdrawBottomSheetNoItemsAlert } from './Alerts/NoItems'
import { DigitalProductStoreWithdrawBottomSheetProvider } from './context'
import { DigitalProductStoreWithdrawBottomSheetRewardRequestsField } from './Fields/RewardRequests'
import { DigitalProductStoreWithdrawBottomSheetFooter } from './Footer'
import { DigitalProductStoreWithdrawBottomSheetHistory } from './History'
import { DigitalProductStoreWithdrawBottomSheetRewardRequestsSkeleton } from './Skeleton'
import { DigitalProductStoreWithdrawBottomSheetTabs } from './Tabs'

// Status exibidos nesta lista global; ignora estados transitórios de claim
// (out_of_stock, insufficient_coins etc.).
const RELEVANT_STATUSES = ['approved', 'delivered', 'canceled', 'expired']

const TABS = [
  { value: 'withdraw', label: 'A retirar' },
  { value: 'history', label: 'Histórico' }
]

export const DigitalProductStoreWithdrawBottomSheet = ({ onHide }) => {
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const [tab, setTab] = useState('withdraw')

  const { digitalProductId, progress } = useDigitalProductContext()
  const { withdraw } = useDigitalProductStore()
  const { hideBottomSheet } = useBottomSheetContext()

  const {
    records: rewardRequests,
    refetch: refetchRewardRequests,
    isLoading: isLoadingRewardRequests
  } = useRecords({
    r: `/user/digital_products/${digitalProductId}/reward_requests`,
    params: {
      filter: {
        progress_id_eq: progress?.id,
        status_in: RELEVANT_STATUSES
      },
      pagination: { per_page: 999 }
    }
  })

  // Chave estável: partição/reset só recalculam quando o conjunto (id+status)
  // realmente muda — evita "Maximum update depth exceeded".
  const recordsKey = rewardRequests
    .map(request => `${request.id}:${request.status}`)
    .join(',')

  const { toWithdraw, history } = useMemo(
    () =>
      partitionRewardRequests(
        rewardRequests.filter(request =>
          RELEVANT_STATUSES.includes(request.status)
        )
      ),
    [recordsKey]
  )

  const methods = useForm({ defaultValues: { reward_requests: [] } })

  const onSubmit = async data => {
    setIsWithdrawing(true)
    const selectedRequests = data.reward_requests.filter(
      request => request.selected
    )
    const selectedRequestsIds = selectedRequests.map(request => request.id)

    await withdraw(selectedRequestsIds)
    setTimeout(() => {
      setIsWithdrawing(false)
      refetchRewardRequests()
    }, 300)
  }

  const toWithdrawKey = toWithdraw.map(request => request.id).join(',')

  useEffect(() => {
    methods.reset({
      reward_requests: toWithdraw.map(request => ({
        ...request,
        selected: true
      }))
    })
  }, [toWithdrawKey])

  // O botão de retirada só faz sentido na aba "A retirar" com itens elegíveis;
  // o histórico é somente leitura.
  const showFooter = tab === 'withdraw' && toWithdraw.length > 0

  return (
    <DigitalProductStoreWithdrawBottomSheetProvider
      isWithdrawing={isWithdrawing}
      setIsWithdrawing={setIsWithdrawing}
    >
      <FormProvider {...methods}>
        <MobileSheet
          open
          onClose={hideBottomSheet}
          title="Suas reservas"
          ariaLabel="Suas reservas"
          className="!max-w-[400px]"
          footer={
            showFooter ? <DigitalProductStoreWithdrawBottomSheet.Footer /> : null
          }
        >
          <DigitalProductStoreWithdrawBottomSheetTabs
            value={tab}
            onChange={setTab}
            tabs={TABS}
          />
          <div className="min-h-[420px]">
            {isLoadingRewardRequests ? (
              <DigitalProductStoreWithdrawBottomSheet.RewardRequestsSkeleton />
            ) : tab === 'withdraw' ? (
              toWithdraw.length === 0 ? (
                <DigitalProductStoreWithdrawBottomSheet.NoRewardRequestsAlert />
              ) : (
                <form id="withdraw" onSubmit={methods.handleSubmit(onSubmit)}>
                  <p>Selecione as reservas que você deseja retirar:</p>
                  <div className="mt-6">
                    <DigitalProductStoreWithdrawBottomSheet.RewardRequestsField
                      refetchRewardRequests={refetchRewardRequests}
                    />
                  </div>
                </form>
              )
            ) : (
              <DigitalProductStoreWithdrawBottomSheet.History
                rewardRequests={history}
                refetchRewardRequests={refetchRewardRequests}
              />
            )}
          </div>
        </MobileSheet>
      </FormProvider>
    </DigitalProductStoreWithdrawBottomSheetProvider>
  )
}

DigitalProductStoreWithdrawBottomSheet.Footer =
  DigitalProductStoreWithdrawBottomSheetFooter
DigitalProductStoreWithdrawBottomSheet.RewardRequestsField =
  DigitalProductStoreWithdrawBottomSheetRewardRequestsField
DigitalProductStoreWithdrawBottomSheet.NoRewardRequestsAlert =
  DigitalProductStoreWithdrawBottomSheetNoItemsAlert
DigitalProductStoreWithdrawBottomSheet.RewardRequestsSkeleton =
  DigitalProductStoreWithdrawBottomSheetRewardRequestsSkeleton
DigitalProductStoreWithdrawBottomSheet.History =
  DigitalProductStoreWithdrawBottomSheetHistory
